import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Users, Calendar, ClipboardCheck, Shield, Globe, CreditCard, CheckCircle2, Sparkles, TrendingUp, Clock, ShieldCheck, Zap } from 'lucide-react'

export const Route = createFileRoute('/feature/ddd')({
  component: AdminFeatureDetailPage,
})

/**
 * ============================================================================
 * DATA STRUCTURE DOCUMENTATION FOR FRONTEND/BACKEND
 * ============================================================================
 * 
 * This component is FLEXIBLE and can accept data from ANY JSON format.
 * The component expects the following structure, but you can map/transform
 * your backend JSON to match this format.
 * 
 * EXPECTED FEATURE INTERFACE:
 * {
 *   id: number                    // Unique identifier for the feature
 *   icon: JSX.Element             // React icon component (can be converted from string/name)
 *   title: string                 // Feature title/name
 *   summary: string               // Short description shown in hero section
 *   keyPoints: string[]           // Array of key feature points (usually 3-5 items)
 *   detailedDescription: string   // Full detailed description (supports multi-paragraph with \n\n)
 * }
 * 
 * FLEXIBLE JSON ACCEPTANCE:
 * -------------------------
 * Your backend can send ANY JSON structure. Examples:
 * 
 * Example 1 - Simple structure:
 * {
 *   "feature_id": 1,
 *   "icon_name": "Users",
 *   "feature_title": "School Management",
 *   "short_desc": "...",
 *   "features": ["point 1", "point 2"],
 *   "full_description": "..."
 * }
 * 
 * Example 2 - Nested structure:
 * {
 *   "data": {
 *     "id": 1,
 *     "metadata": {
 *       "title": "...",
 *       "description": "..."
 *     },
 *     "details": {
 *       "summary": "...",
 *       "points": [...]
 *     }
 *   }
 * }
 * 
 * Example 3 - Array from API:
 * [
 *   { "feature_id": 1, "name": "...", "icon": "Users", ... },
 *   { "feature_id": 2, "name": "...", "icon": "Calendar", ... }
 * ]
 * 
 * MAPPING FUNCTION:
 * -----------------
 * You can create a mapper function to transform your backend JSON to this format:
 * 
 * function mapApiResponseToFeature(apiData: any): Feature {
 *   return {
 *     id: apiData.feature_id || apiData.id,
 *     icon: getIconComponent(apiData.icon_name || apiData.icon),
 *     title: apiData.feature_title || apiData.name || apiData.title,
 *     summary: apiData.short_desc || apiData.summary,
 *     keyPoints: apiData.features || apiData.keyPoints || apiData.points || [],
 *     detailedDescription: apiData.full_description || apiData.detailedDescription
 *   }
 * }
 * 
 * ICON HANDLING:
 * --------------
 * If your backend sends icon names as strings (e.g., "Users", "Calendar"),
 * you can create a mapping function:
 * 
 * const iconMap: Record<string, JSX.Element> = {
 *   "Users": <Users className="w-8 h-8" />,
 *   "Calendar": <Calendar className="w-8 h-8" />,
 *   // ... add more icons
 * }
 * 
 * Then use: icon: iconMap[apiData.icon_name] || <DefaultIcon />
 * 
 * DATA SOURCE:
 * -----------
 * Currently using static mock data for preview. In production, replace with:
 * 
 * 1. API call with fetch/axios:
 *    const response = await fetch('/api/features')
 *    const apiData = await response.json()
 *    const features = apiData.map(mapApiResponseToFeature)
 * 
 * 2. React Query:
 *    const { data: apiData } = useQuery(['features'], fetchFeatures)
 *    const features = apiData?.map(mapApiResponseToFeature) || []
 * 
 * 3. TanStack Router loader:
 *    export const Route = createFileRoute('/feature/ddd')({
 *      loader: async () => {
 *        const apiData = await fetchFeatures()
 *        return { features: apiData.map(mapApiResponseToFeature) }
 *      },
 *      component: AdminFeatureDetailPage
 *    })
 * 
 * BACKEND API ENDPOINTS & CURL COMMANDS:
 * --------------------------------------
 * The backend provides REST API endpoints for creating and updating features.
 * Below are curl commands for testing/managing features (English & French only).
 * 
 * BACKEND JSON STRUCTURE:
 * {
 *   "roles": ["SuperAdmin", "Admin", "Teacher"],
 *   "name": {
 *     "en": { "name": "Feature Name" },
 *     "fr": { "name": "Nom de la fonctionnalité" }
 *   },
 *   "description": {
 *     "en": {
 *       "points": {
 *         "point1": "Key point 1",
 *         "point2": "Key point 2",
 *         "point3": "Key point 3"
 *       },
 *       "description": "Detailed description text",
 *       "website": { "title": "Optional nested data" }
 *     },
 *     "fr": {
 *       "points": {
 *         "point1": "Point clé 1",
 *         "point2": "Point clé 2",
 *         "point3": "Point clé 3"
 *       },
 *       "description": "Texte de description détaillé",
 *       "website": { "title": "Données imbriquées optionnelles" }
 *     }
 *   },
 *   "images": ["https://cdn.jeridschool.com/files/example1.jpg"],
 *   "isActive": true,
 *   "order": 1
 * }
 * 
 * 1. CREATE FEATURE (JSON):
 * --------------------------
 * curl -X POST "http://localhost:3000/features" \\
 *   -H "Content-Type: application/json" \\
 *   -d '{
 *     "roles": ["SuperAdmin", "Admin", "Teacher"],
 *     "name": {
 *       "en": {
 *         "name": "Advanced Analytics"
 *       },
 *       "fr": {
 *         "name": "Analyse Avancée"
 *       }
 *     },
 *     "description": {
 *       "en": {
 *         "points": {
 *           "point1": "Track student progress",
 *           "point2": "Monitor attendance",
 *           "point3": "Generate reports"
 *         },
 *         "description": "Get detailed insights into your school performance",
 *         "website": {
 *           "title": "Website Title",
 *           "fields": {
 *             "hostname": "Hostname:",
 *             "schoolName": "School Name:",
 *             "fullUrl": "Full URL:"
 *           },
 *           "values": {
 *             "yes": "Yes",
 *             "no": "No",
 *             "available": "Available"
 *           }
 *         }
 *       },
 *       "fr": {
 *         "points": {
 *           "point1": "Suivre les progrès des étudiants",
 *           "point2": "Surveiller la présence",
 *           "point3": "Générer des rapports"
 *         },
 *         "description": "Obtenez des informations détaillées sur les performances de votre école",
 *         "website": {
 *           "title": "Titre du site",
 *           "fields": {
 *             "hostname": "Nom d'\''hôte :",
 *             "schoolName": "Nom de l'\''école :",
 *             "fullUrl": "URL complète :"
 *           },
 *           "values": {
 *             "yes": "Oui",
 *             "no": "Non",
 *             "available": "Disponible"
 *           }
 *         }
 *       }
 *     },
 *     "images": ["https://cdn.jeridschool.com/files/example1.jpg"],
 *     "isActive": true,
 *     "order": 1
 *   }'
 * 
 * 2. CREATE FEATURE (Multipart with file uploads):
 * -------------------------------------------------
 * curl -X POST "http://localhost:3000/features" \\
 *   -F 'roles=["SuperAdmin","Admin","Student"]' \\
 *   -F 'name={"en":{"name":"File Upload Test"},"fr":{"name":"Test Upload"}}' \\
 *   -F 'description={"en":{"points":{"point1":"Point 1"},"description":"Testing","website":{"title":"Website","fields":{},"values":{}}},"fr":{"points":{"point1":"Point 1"},"description":"Test","website":{"title":"Site web","fields":{},"values":{}}}}' \\
 *   -F 'isActive=true' \\
 *   -F 'order=2' \\
 *   -F 'images=@/path/to/image1.jpg' \\
 *   -F 'images=@/path/to/image2.png'
 * 
 * 3. UPDATE FEATURE (JSON):
 * --------------------------
 * curl -X PATCH "http://localhost:3000/features/YOUR_FEATURE_ID_HERE" \\
 *   -H "Content-Type: application/json" \\
 *   -d '{
 *     "name": {
 *       "en": {
 *         "name": "Updated Feature Name"
 *       },
 *       "fr": {
 *         "name": "Nom mis à jour"
 *       }
 *     },
 *     "description": {
 *       "en": {
 *         "points": {
 *           "point1": "Updated point 1",
 *           "point2": "Updated point 2",
 *           "point3": "New point 3"
 *         },
 *         "description": "Updated description text",
 *         "website": {
 *           "title": "Updated Website Title",
 *           "fields": {
 *             "hostname": "Hostname:",
 *             "isSubdomain": "Is Subdomain:",
 *             "schoolName": "School Name from Subdomain:",
 *             "fullUrl": "Full URL:",
 *             "apiBaseUrl": "API Base URL:",
 *             "authToken": "Auth Token:"
 *           },
 *           "values": {
 *             "yes": "Yes",
 *             "no": "No",
 *             "available": "Available",
 *             "notAvailable": "Not Available",
 *             "none": "none"
 *           },
 *           "apiStatus": {
 *             "title": "API Endpoint Status:",
 *             "publicEndpoint": "Public Endpoint:",
 *             "protectedEndpoint": "Protected Endpoint (no auth):",
 *             "protectedEndpointWithAuth": "Protected Endpoint (with auth):",
 *             "states": {
 *               "notTested": "Not Tested",
 *               "accessible": "Accessible",
 *               "noAuthToken": "No Auth Token Available",
 *               "error": "Error: {{status}} - {{message}}"
 *             }
 *           },
 *           "testing": {
 *             "publicEndpoint": "Testing Public Endpoint: {{url}}",
 *             "protectedEndpoint": "Testing Protected Endpoint (no auth): {{url}}",
 *             "protectedEndpointWithAuth": "Testing Protected Endpoint (with auth): {{url}}"
 *           },
 *           "errors": {
 *             "publicEndpoint": "Public Endpoint Error: {{error}}",
 *             "protectedEndpoint": "Protected Endpoint Error: {{error}}",
 *             "protectedEndpointWithAuth": "Protected Endpoint with Auth Error: {{error}}"
 *           }
 *         }
 *       },
 *       "fr": {
 *         "points": {
 *           "point1": "Point mis à jour 1",
 *           "point2": "Point mis à jour 2"
 *         },
 *         "description": "Description mise à jour",
 *         "website": {
 *           "title": "Titre mis à jour",
 *           "fields": {
 *             "hostname": "Nom d'\''hôte :",
 *             "isSubdomain": "Est un sous-domaine :"
 *           },
 *           "values": {
 *             "yes": "Oui",
 *             "no": "Non"
 *           }
 *         }
 *       }
 *     },
 *     "isActive": false,
 *     "order": 5
 *   }'
 * 
 * 4. UPDATE FEATURE (Multipart with file upload):
 * -----------------------------------------------
 * curl -X PATCH "http://localhost:3000/features/YOUR_FEATURE_ID_HERE" \\
 *   -F 'name={"en":{"name":"Updated Name"},"fr":{"name":"Nom mis à jour"}}' \\
 *   -F 'description={"en":{"points":{"point1":"Point 1"},"description":"Updated","website":{"title":"Website","fields":{"field1":"Field 1"},"values":{"yes":"Yes","no":"No"}}},"fr":{"points":{"point1":"Point 1"},"description":"Mis à jour","website":{"title":"Site web","fields":{"field1":"Champ 1"},"values":{"yes":"Oui","no":"Non"}}}}' \\
 *   -F 'isActive=true' \\
 *   -F 'order=3' \\
 *   -F 'images=@/path/to/new-image.jpg'
 * 
 * NOTES:
 * ------
 * - Replace YOUR_FEATURE_ID_HERE with an actual feature ID
 * - Adjust file paths in multipart examples to match your local files
 * - The website field accepts any JSON structure (nested objects, arrays, etc.)
 * - Only English (en) and French (fr) are included - Arabic (ar) is excluded
 * - The description field supports any additional fields you want to add
 * - Base URL: http://localhost:3000 (update for production)
 * 
 * ============================================================================
 */

interface Feature {
  id: number
  icon: JSX.Element
  title: string
  summary: string
  keyPoints: string[]
  detailedDescription: string
}

/**
 * Helper function to map icon names (strings) to React components
 * Use this if your backend sends icon names instead of JSX elements
 */
function getIconComponent(iconName: string): JSX.Element {
  const iconMap: Record<string, JSX.Element> = {
    Users: <Users className="w-8 h-8" />,
    Calendar: <Calendar className="w-8 h-8" />,
    ClipboardCheck: <ClipboardCheck className="w-8 h-8" />,
    Shield: <Shield className="w-8 h-8" />,
    Globe: <Globe className="w-8 h-8" />,
    CreditCard: <CreditCard className="w-8 h-8" />,
  }
  return iconMap[iconName] || <Users className="w-8 h-8" /> // Default icon
}

/**
 * Mapper function to transform backend JSON to Feature interface
 * 
 * This function handles the backend's multilingual structure:
 * - name: { en: { name: "..." }, fr: { name: "..." } }
 * - description: { en: { points: {...}, description: "..." }, fr: {...} }
 * 
 * It extracts data based on the current language (defaults to English).
 * 
 * @param apiData - Your backend JSON response (any structure)
 * @param language - Language code ('en' or 'fr'), defaults to 'en'
 * @returns Feature object matching the expected interface
 * 
 * @example Basic usage:
 * const apiResponse = await fetch('/api/features').then(r => r.json())
 * const features = apiResponse.map(data => mapApiResponseToFeature(data, 'en'))
 * 
 * @example With current language from i18n:
 * const { i18n } = useTranslation()
 * const features = apiResponse.map(data => mapApiResponseToFeature(data, i18n.language))
 */
// Export for use by frontend developers when integrating with API
export function mapApiResponseToFeature(apiData: any, language: 'en' | 'fr' = 'en'): Feature {
  // Handle backend multilingual structure
  const nameData = apiData.name?.[language] || apiData.name?.en || apiData.name
  const descData = apiData.description?.[language] || apiData.description?.en || apiData.description
  
  // Extract title from multilingual structure or fallback to simple fields
  const title = nameData?.name || 
                apiData.feature_title || 
                apiData.name || 
                apiData.title || 
                ''
  
  // Extract summary/description from multilingual structure
  const summary = descData?.description || 
                  apiData.short_desc || 
                  apiData.summary || 
                  apiData.shortDescription || 
                  ''
  
  // Extract key points from multilingual structure
  let keyPoints: string[] = []
  if (descData?.points) {
    // Convert points object { point1: "...", point2: "..." } to array
    keyPoints = Object.values(descData.points) as string[]
  } else {
    // Fallback to various possible field names
    keyPoints = apiData.features || 
                apiData.keyPoints || 
                apiData.points || 
                apiData.key_features || 
                []
  }
  
  // Extract detailed description
  const detailedDescription = descData?.description || 
                             apiData.full_description || 
                             apiData.detailedDescription || 
                             apiData.description || 
                             apiData.fullDescription || 
                             ''
  
  return {
    id: apiData.feature_id || apiData.id || apiData.featureId || 0,
    icon: apiData.icon || getIconComponent(apiData.icon_name || apiData.iconName || apiData.icon || 'Users'),
    title,
    summary,
    keyPoints: Array.isArray(keyPoints) ? keyPoints : [],
    detailedDescription
  }
}

// Feature data - in production, this would come from an API
// Replace this array with your API call and use mapApiResponseToFeature() to transform the data
const features: Feature[] = [
  {
    id: 1,
    icon: <Users className="w-8 h-8" />,
    title: 'School Management System',
    summary: "The School Management System is the backbone of JeridSchool's Admin panel. It provides the Admin with complete control over every academic and operational aspect of the school. From one central dashboard, the Admin can manage students, teachers, parents, grades, classes, subjects, and rooms — all in one organized digital ecosystem.",
    keyPoints: [
      'Complete control over students, teachers, parents, grades, classes, subjects, and rooms from one central dashboard',
      'Track student subscription payments and manage bulk imports or individual student profiles',
      'Multilingual support for Arabic, French, and English with total digital control eliminating paper files and manual scheduling'
    ],
    detailedDescription: `The Admin begins by setting up the school's structure, including grade levels (for example, primary, secondary, or university), and defining the class groups within each grade. Each class can then be assigned its own homeroom teacher, subjects, and student list.

Next, the Admin manages subjects — creating or importing them, defining their weekly hours, and linking each subject to a qualified teacher. This ensures that every class has a clear academic schedule with no overlap or confusion.

The system also allows the creation and organization of rooms, including classrooms, laboratories, computer labs, and even virtual rooms for online sessions. Each room can be assigned a capacity, availability schedule, and purpose, helping avoid conflicts or double-booking.

Parent management is another key part of the Admin's role. Parents are added and linked directly to their child's profile, allowing them to monitor attendance, assignments, and progress in real time. The platform automatically synchronizes parent-child relationships and sends notifications when updates occur.

Students can be imported in bulk or added individually, with full profiles including personal data, academic level, and class assignments. If a student changes grade or transfers, the Admin can easily update their record without losing any history. You can also track if this student has paid their subscription or still has not.

The Admin also oversees teachers, assigning them to subjects and classes, defining schedules, and tracking their teaching load. This helps maintain a balanced and transparent system where everyone's responsibilities are clear.

All data is interconnected — changing a subject, teacher, or room automatically updates related records, ensuring accuracy and consistency across the system. The interface is designed for ease of use, with multilingual support for Arabic, French, and English, making it ideal for Tunisian institutions.

The main benefit is total digital control — no more paper files, Excel sheets, or manual scheduling. The Admin gains a real-time view of the entire school, improving efficiency, accuracy, and communication. JeridSchool's School Management System transforms complex administrative tasks into a smooth, visual, and intelligent management experience, empowering schools to operate more efficiently and focus on education rather than paperwork.`
  },
  {
    id: 2,
    icon: <Calendar className="w-8 h-8" />,
    title: 'Timetable Management System',
    summary: "The Timetable Management System in JeridSchool allows the Admin to design, organize, and control the entire school schedule with complete flexibility and accuracy. It's the tool that ensures every class, subject, and teacher fits perfectly into the weekly routine without confusion or overlap.",
    keyPoints: [
      'Flexible subject distribution across the week with drag-and-drop visual timetable interface',
      'Intelligent validation rules prevent conflicts (teacher double-booking, room conflicts)',
      'Real-time updates available to teachers, students, and parents with instant synchronization across all records'
    ],
    detailedDescription: `From the dashboard, the Admin can select any grade or class group and begin assigning subjects and teachers according to the school's available days and hours. The Admin chooses which days each teacher works, the number of study hours per subject, and the method of distribution across the week.

For example, if a subject like Mathematics has 5 weekly hours, the Admin can divide it as 2 hours on Monday, 2 on Wednesday, and 1 on Friday — or any pattern that suits the class schedule. This flexibility allows each school to follow its own teaching rhythm while keeping a balanced load for students and teachers.

The system also supports multiple class groups, meaning the Admin can assign different subjects or teachers to different sections of the same grade. Each timetable adapts automatically to fit those configurations.

JeridSchool's interface uses a clear visual timetable view, where the Admin can drag, drop, or modify subjects and hours easily. Any changes — such as replacing a teacher or switching a classroom — are updated instantly and reflected across all related records.

Importantly, the system includes intelligent validation rules that prevent human errors. For instance, it will not allow a teacher to teach two classes at the same time, or assign a room to two groups simultaneously. It also checks that subject hours are correctly distributed according to the school's academic plan.

The Admin can view the timetable in a daily or weekly layout to verify balance and identify free periods. Adjustments can be made at any time, and the interface ensures every update is accurate and consistent.

Once finalized, the timetable becomes immediately available to teachers, students, and parents, ensuring everyone knows exactly when and where lessons occur.

This system eliminates scheduling chaos, paperwork, and misunderstandings. It gives schools total control over teaching hours, resources, and time — creating a smooth, fair, and transparent academic routine for everyone involved.`
  },
  {
    id: 3,
    icon: <ClipboardCheck className="w-8 h-8" />,
    title: 'Attendance Management System',
    summary: 'The Attendance Management System in JeridSchool provides the Admin with full control and visibility over student and teacher attendance across all grades, subjects, and classes. It ensures that every session is properly tracked, making the learning process more accountable and transparent.',
    keyPoints: [
      'Real-time attendance tracking with automatic synchronization from teacher dashboards eliminating paper sheets',
      'Filter attendance by class, subject, teacher, or date with analytics including monthly absence rates and punctuality trends',
      'Automated parent notifications for absences and ability to assign substitute teachers ensuring lessons continue without interruption'
    ],
    detailedDescription: `Each day, teachers mark attendance directly from their own dashboard, selecting which students are present, absent, or late. This information is automatically synchronized with the Admin's dashboard in real time, eliminating the need for paper sheets or manual collection.

From the Admin interface, attendance can be filtered by class, subject, teacher, or specific date, giving a clear and structured view of daily and historical data. The Admin can instantly identify which students are consistently late, frequently absent, or have justified excuses.

If an error occurs or a student's absence is justified (for example, due to illness or school permission), the Admin can edit or approve the record with just a few clicks. This flexibility keeps the system accurate and up to date.

The Admin also has the ability to assign substitute teachers when a teacher is planned to be AFK (away from class) — ensuring that lessons continue without interruption. The system records both the original and replacement teachers for accountability and scheduling purposes.

In addition, JeridSchool generates attendance analytics and statistics, such as monthly absence rates, punctuality trends, and student participation summaries. These reports help identify behavior patterns and assist the school in providing early support to at-risk students.

Parents automatically receive notifications when their child is marked absent or late, strengthening communication between home and school and encouraging responsibility among students.

The attendance data also connects seamlessly with other systems — for example, timetable and performance analytics — to ensure consistency across the entire platform.

With JeridSchool, attendance tracking becomes a smart, paperless, and real-time process, freeing Admins from manual work and ensuring complete transparency. It allows schools to maintain discipline, improve accountability, and react quickly to any attendance-related issues.

Ultimately, the Attendance Management System transforms daily record-keeping into valuable insights, helping schools create a more organized, reliable, and efficient academic environment.`
  },
  {
    id: 4,
    icon: <Shield className="w-8 h-8" />,
    title: 'SuperAdmin – Manage Admins',
    summary: 'The SuperAdmin is the top-level user in JeridSchool with full authority over the entire platform. One of their key responsibilities is to manage Admin accounts for multiple schools. They can create, edit, or deactivate Admins, assign roles, and control their access to specific school data.',
    keyPoints: [
      'Create, edit, or deactivate Admin accounts for multiple schools with centralized dashboard monitoring',
      'Reset passwords, update permissions, and provide support ensuring efficient and secure Admin operations',
      'Maintain standardized operational structure across all schools enforcing consistent policies and data security'
    ],
    detailedDescription: `The SuperAdmin is the top-level user in JeridSchool with full authority over the entire platform. One of their key responsibilities is to manage Admin accounts for multiple schools. They can create, edit, or deactivate Admins, assign roles, and control their access to specific school data.

Through a centralized dashboard, the SuperAdmin monitors Admin activity, ensuring that each Admin performs their duties efficiently and securely. They can reset passwords, update permissions, and provide support when needed.

This oversight ensures consistency across all schools using JeridSchool, allowing the SuperAdmin to maintain a standardized operational structure and enforce school policies effectively.

By managing Admins, the SuperAdmin guarantees that schools operate smoothly, data remains secure, and each Admin can focus on running their school without technical interruptions.`
  },
  {
    id: 5,
    icon: <Globe className="w-8 h-8" />,
    title: 'SuperAdmin – Manage School Websites',
    summary: "Another key responsibility of the SuperAdmin is to manage the school websites for each institution using JeridSchool. They can create and customize websites for different schools, ensuring that each site reflects the school's identity, branding, and essential information.",
    keyPoints: [
      'Customize school websites with logo, colors, navigation menus, and homepage layout with multilingual support (Arabic, French, English)',
      'Manage content sections (About Us, Admissions, News, Events, Contact) and enable/disable features like online registration',
      'Monitor website performance and analytics while maintaining security and quality across all school websites'
    ],
    detailedDescription: `Another key responsibility of the SuperAdmin is to manage the school websites for each institution using JeridSchool. They can create and customize websites for different schools, ensuring that each site reflects the school's identity, branding, and essential information.

The SuperAdmin controls website settings such as the school's logo, colors, navigation menus, and homepage layout. They can also manage content sections like About Us, Admissions, News, Events, and Contact information.

Through this system, SuperAdmins can enable or disable features such as online registration forms, news feeds, and event calendars. They can also manage multiple languages (Arabic, French, English) to accommodate students, parents, and staff in Tunisia.

They can assign Admins or content managers to update website content regularly while retaining ultimate control over what is published. This ensures that all public information is accurate, up to date, and aligned with the school's standards.

Additionally, the SuperAdmin can monitor website performance, view analytics such as visitor counts and popular pages, and make improvements to enhance user experience.

This feature allows schools to maintain a professional online presence without needing separate IT teams, while the SuperAdmin ensures security, consistency, and quality across all school websites.

By managing both Admin accounts and school websites, the SuperAdmin guarantees that JeridSchool's platform is efficient, organized, and visually appealing, giving schools a competitive advantage in education management and communication.`
  },
  {
    id: 6,
    icon: <CreditCard className="w-8 h-8" />,
    title: 'Student Card Configuration',
    summary: 'Configure student cards with essential information including name, contact details, academic year, and school logo. This ensures the identity of the school is properly displayed across the system, making student identification and management seamless.',
    keyPoints: [
      'Customize student cards with name, contact details, academic year, and school logo for proper identification',
      'Ensure school identity is consistently displayed across all system modules and interfaces',
      'Streamline student data management with centralized card configuration and automated updates'
    ],
    detailedDescription: `Configure student cards with essential information including name, contact details, academic year, and school logo. This ensures the identity of the school is properly displayed across the system, making student identification and management seamless.

After setup, the student cards serve as a comprehensive identification system that displays consistently across all modules and interfaces. The school's branding and identity are properly represented, creating a professional and cohesive experience throughout the platform.

This centralized card configuration streamlines student data management, allowing for easy updates and ensuring that all information remains synchronized across the entire system.`
  }
]

function AdminFeatureDetailPage() {
  // ============================================================================
  // DATA INTEGRATION GUIDE FOR FRONTEND DEVELOPERS
  // ============================================================================
  // 
  // CURRENT SETUP: Using static mock data for preview
  // 
  // TO CONNECT TO YOUR API:
  // 1. Replace the static 'features' array with your API call
  // 2. Use the mapApiResponseToFeature() function to transform your JSON
  // 3. The component is flexible - it accepts ANY JSON structure from your backend
  //
  // Example with React Query:
  //   const { data: apiResponse } = useQuery(['features'], fetchFeaturesFromAPI)
  //   const features = apiResponse?.map(mapApiResponseToFeature) || []
  //
  // Example with useEffect:
  //   const [features, setFeatures] = useState<Feature[]>([])
  //   useEffect(() => {
  //     fetch('/api/features')
  //       .then(res => res.json())
  //       .then(data => setFeatures(data.map(mapApiResponseToFeature)))
  //   }, [])
  //
  // Example with TanStack Router loader (recommended):
  //   export const Route = createFileRoute('/feature/ddd')({
  //     loader: async () => {
  //       const apiData = await fetch('/api/features').then(r => r.json())
  //       return { features: apiData.map(mapApiResponseToFeature) }
  //     },
  //     component: AdminFeatureDetailPage
  //   })
  //   Then in component: const { features } = useLoaderData({ from: Route.fullPath })
  //
  // ============================================================================
  
  // For static preview, show the first feature by default
  // In production, you'd get the id from route params: const { id } = useParams({ from: Route.fullPath })
  const feature = features[0] // Show first feature as static mock data
  
  // If using API, uncomment and adapt:
  // const { id } = useParams({ from: Route.fullPath })
  // const featureId = parseInt(id || '0', 10)
  // const feature = features.find(f => f.id === featureId)
  
  const mainColor = '#525FE1'

  if (!feature) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">No Features Available</h1>
          <p className="text-gray-600 mb-6">No features found to display.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Hero Section */}
      <div 
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${mainColor} 0%, ${mainColor}dd 50%, ${mainColor}cc 100%)`
        }}
        >
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
        <div className="container mx-auto px-4 py-16 md:py-24 relative">
          {/* Back Button */}
          <Link
            to="/feature/admin"
            className="inline-flex items-center text-white/90 hover:text-white mb-8 transition-colors text-sm font-medium backdrop-blur-sm bg-white/10 px-4 py-2 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Features
          </Link>
          
          {/* Hero Content */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm mb-8 shadow-xl border border-white/30">
              <div className="text-white">
                {feature.icon}
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {feature.title}
            </h1>
            <p className="text-xl text-white/95 leading-relaxed max-w-3xl mx-auto">
              {feature.summary}
            </p>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-32 h-32 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-40 h-40 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-5xl mx-auto">
          {/* Key Points Section - Enhanced */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-12 mb-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-5" style={{ backgroundColor: mainColor, transform: 'translate(30%, -30%)' }}></div>
            
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${mainColor}15` }}>
                <Sparkles className="w-6 h-6" style={{ color: mainColor }} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Key Features
              </h2>
            </div>
            
            <div className="grid md:grid-cols-1 gap-6 relative z-10">
              {feature.keyPoints.map((point, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-4 p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200 bg-gradient-to-r from-gray-50/50 to-transparent"
                >
                  <div className="flex-shrink-0 mt-1">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${mainColor}15` }}
                    >
                      <CheckCircle2 className="w-5 h-5" style={{ color: mainColor }} />
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-base font-medium flex-1 pt-1">
                    {point}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Overview Section */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-12 mb-8 relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-5" style={{ backgroundColor: mainColor, transform: 'translate(-30%, 30%)' }}></div>
            
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${mainColor}15` }}>
                <TrendingUp className="w-6 h-6" style={{ color: mainColor }} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Detailed Overview
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none relative z-10">
              <div className="text-gray-700 leading-relaxed whitespace-pre-line space-y-4 text-base">
                {feature.detailedDescription.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>

          {/* Benefits Highlight Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${mainColor}15` }}>
                <Zap className="w-6 h-6" style={{ color: mainColor }} />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Efficient</h3>
              <p className="text-sm text-gray-600">Streamline operations with automated workflows and real-time updates.</p>
            </div>
            
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${mainColor}15` }}>
                <ShieldCheck className="w-6 h-6" style={{ color: mainColor }} />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Secure</h3>
              <p className="text-sm text-gray-600">Enterprise-grade security with role-based access control.</p>
            </div>
            
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${mainColor}15` }}>
                <Clock className="w-6 h-6" style={{ color: mainColor }} />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Real-time</h3>
              <p className="text-sm text-gray-600">Instant synchronization across all devices and users.</p>
            </div>
          </div>

          {/* Call to Action */}
          <div 
            className="rounded-2xl p-8 md:p-12 text-center relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${mainColor} 0%, ${mainColor}dd 100%)`
            }}
          >
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                Experience the power of {feature.title} and transform how you manage your school operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 hover:shadow-xl"
                  style={{ color: mainColor }}
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/demo"
                  className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-3 rounded-lg font-semibold transition-all hover:bg-white/20"
                >
                  Schedule Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}